/**
 * Loan type definitions
 */

import { Repayment } from './repayment.types';
import { NachRepayment } from './nach.types';

// ============================================================================
// LOAN STATUS ENUMS
// ============================================================================

export type LoanStatus = 
  | 'Active' 
  | 'InArrears' 
  | 'Paused' 
  | 'PaidOff' 
  | 'Cancelled' 
  | 'Approved' 
  | 'Rejected'
  | 'MerchantRefund'
  | 'RefundInitiated';

export type QuickPayStatus = 0 | 1 | 2 | 3;
// 0 = Hidden, 1 = Active, 2 = Inactive (NACH pending), 3 = Processing

export type MoratoriumStatus = 
  | 'INELIGIBLE' 
  | 'ELIGIBLE' 
  | 'OPTIN_IN_PROGRESS' 
  | 'OPTIN_COMPLETED' 
  | 'OPTOUT_IN_PROGRESS' 
  | 'OPTOUT_COMPLETED'
  | null;

export enum GiftCardGenerationStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  PARTIAL_FAILURE = 'PARTIAL_FAILURE',
  PENDING = 'PENDING'
}

// ============================================================================
// CORE LOAN INTERFACES
// ============================================================================

export interface Loan {
  mambuId: string;
  CustomerId: string;
  IsLoanDisbursed: boolean;
  NextDueAmount: number;
  NumberOfPaidRepayments: number;
  NumberOfRemainingRepayments: number;
  PrincipalPaid: number;
  TotalDueAmount: number;
  TotalExpectedAmountAsOfToday: number;
  TotalPaidAmount: number;
  application?: LoanApplication;
  ExternalId: string;
  LoanExternalId: string;
  MerchantOrderId: string;
  MerchantName: string;
  BasketSize: number;
  Status: LoanStatus;
  logoUrl: string;
  ProductNames: string[];
  LoanAmount: number;
  EmiAmount: number;
  OrderId: string;
  NumbersOfMonth: number;
  AmountPaid: number;
  NextDueDate: string;
  Amount: number;
  TotalInstallmentAmount: number;
  Terms: number;
  CreatedOn: string;
  Repayments: Repayment[];
  TotalAmount: number;
  nachRepayment: NachRepayment | null;
  nachRepaymentArray: NachRepayment[];
  nachRepaymentError: boolean;
  showQuickPay: QuickPayStatus;
  MerchantDetails: MerchantDetails | null;
  MerchantSettingResponse?: MerchantSettingResponse;
  FeeAndPenaltyDue: number;
  showPreclose: number;
  insuranceDetails: InsuranceDetails | null;
  MoratoriumStatus: MoratoriumStatus;
  MoratoriumInterestDue: number | null;
  Processing: boolean;
  restructured: boolean;
  latePaymentAmount: number;
}

export interface MerchantDetails {
  IsExtendedApiMerchant: boolean;
  IsVoucherMerchant: boolean;
  MerchantUrl: string;
  MerchantId: string;
}

export interface MerchantSettingResponse {
  MerchantId: string;
  MerchantName?: string;
}

export interface InsuranceDetails {
  insuranceFee: number;
  insuranceFeePercent: number;
  loanId: string;
  partnerId: string;
  partnerName: string;
  policyId: string;
  policyNumber: string;
}

// ============================================================================
// LOAN APPLICATION INTERFACES
// ============================================================================

export interface LoanApplication {
  ApplicationId: string;
  ApprovedUrl: string;
  BasketAmount: number;
  BasketItems: BasketItem[];
  CreatedOn: string;
  CustomerId: string;
  CustomerRank: string | null;
  DownpaymentAmount: number;
  DownpaymentRate: string;
  InitialPaymentAmount: number;
  InterestAmount: number;
  InterestFreeMonths: number;
  InterestRate: number;
  IsCODEnabled: boolean;
  IsDPMade: boolean;
  IsDownpaymentProvidedByMerchant: boolean;
  IsRiskApproved: boolean;
  IsRiskDeclined: boolean;
  LoanAmount: number;
  MerchantId: string;
  MerchantName: string;
  GenericMerchantName: string;
  MerchantOrderId: string;
  NumberOfInstallments: number;
  ProcessingFee: number;
  ProcessingFeeRate: number;
  ReturnUrl: string;
  Status: number;
  TotalInstallmentAmount: number;
  PaymentMode: string;
  ShowKycUnderLimitByDefault: boolean;
  IsExtendedApiMerchant: boolean;
  IsVoucherMerchant: boolean;
  MerchantUrl: string;
}

export interface BasketItem {
  Category: string;
  Description: string;
  MerchantItemId: string;
  Quantity: number;
  TotalPrice: number;
}

// ============================================================================
// EMI & PAYMENT INTERFACES
// ============================================================================

export interface EmiDue {
  amount: number;
  date: string | null;
  index: number | null;
  feesDue: number;
  dueAmount?: number;
}

export interface Fee {
  LateFee: number;
  ProcessingFee: number;
  InsuranceFee: number;
  TotalFees: number;
  FeesPaid: number;
}

export interface PrecloseLoanDetails {
  Principle: number;
  Interests: number;
  Fees: number;
  Penalties: number;
  Total: number;
}

// ============================================================================
// GIFT CARD / VOUCHER INTERFACES
// ============================================================================

export interface GiftCardDetails {
  voucherCode: string;
  voucherPin: string;
  voucherValue: number;
  voucherValidity: number;
  merchantName: string;
  errorSegment: GiftCardDetailsErrorSegment | null;
}

export interface GiftCardDetailsErrorSegment {
  result: GiftCardGenerationStatus;
  ErrorMessage: string;
}

export interface GiftCardVouchers {
  giftCardDetails: GiftCardDetails[];
  giftCardGenerationStatus: GiftCardGenerationStatus;
}

export interface GCToken {
  token: string;
}

// ============================================================================
// STOP DEBIT INTERFACES
// ============================================================================

export interface StopDebit {
  LoanId: string;
  CustomerId: string;
  StopDebitDate: string;
  StopDebitReason: string;
  Comments: string;
}

export interface StopDebitStatus {
  StopDebitDate: string;
  StopDebitForDueDate: string;
  PaymentStatus: string;
}

// ============================================================================
// NOTIFICATION & UI INTERFACES
// ============================================================================

export interface NotificationBar {
  barHeading: string;
  description: string;
  subDescription: string;
  ctaLabel: string;
}

export interface MandateStatus {
  bankDetailsRequired: boolean;
  mandateSetupRequired: boolean;
  creditLimitValid: boolean;
  kycCompleted: boolean;
}

export interface PartnerDetails {
  partnerId: string;
  partnerName: string;
  merchantName: string;
}

// ============================================================================
// BOLT INTERFACES
// ============================================================================

export interface BoltCta {
  href: string;
  target: string;
  text: string;
  event: string;
  type: string;
}

// ============================================================================
// LENDER INTERFACES
// ============================================================================

export interface LenderInfo {
  lenderMetaData?: {
    registeredName: string;
    grievancePolicyURL: string;
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function createDefaultEmiDue(): EmiDue {
  return {
    amount: 0,
    date: null,
    index: null,
    feesDue: 0,
    dueAmount: 0
  };
}

export function createDefaultFee(): Fee {
  return {
    LateFee: 0,
    ProcessingFee: 0,
    InsuranceFee: 0,
    TotalFees: 0,
    FeesPaid: 0
  };
}

export function createDefaultMandateStatus(): MandateStatus {
  return {
    bankDetailsRequired: false,
    mandateSetupRequired: false,
    creditLimitValid: false,
    kycCompleted: false
  };
}

export function createDefaultPrecloseLoanDetails(): PrecloseLoanDetails {
  return {
    Principle: 0,
    Interests: 0,
    Fees: 0,
    Penalties: 0,
    Total: 0
  };
}
